import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { FormsGate } from './components';

// Eager load dashboard for instant access
import FormsDashboard from './dashboard';

// Lazy load all other pages
const FormsListPage = lazy(() => import('./forms'));
const CreateFormPage = lazy(() => import('./forms/create'));
const FormDetailPage = lazy(() => import('./forms/detail'));
const ApprovalsPage = lazy(() => import('./approvals'));
const WebhooksPage = lazy(() => import('./webhooks'));

// Admin pages
const AdminSettingsPage = lazy(() => import('./admin/settings'));
const AdminLookupsPage = lazy(() => import('./admin/lookups'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Forms Management Routes
 *
 * Module access (Admin / FormsAdmin / FormsManager / FormsReviewer / FormsViewer) is the
 * domain gate; inside, pages gate on capabilities (role OR Forms.* right) via FormsGate,
 * and per-form actions on the form's accessLevelID.
 */
export default function FormsManagementRoutes() {
  const location = useLocation();
  // The builder canvas gets the full width (TaskManagement does the same for /board).
  const forceFullWidth = /\/forms\/\d+\/builder/.test(location.pathname);

  return (
    <MainLayout domainKey="forms" forceFullWidth={forceFullWidth}>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<FormsDashboard />} />

          {/* Forms */}
          <Route path="/forms" element={<FormsListPage />} />
          <Route
            path="/forms/create"
            element={
              <FormsGate allow={(p) => p.canCreate}>
                <CreateFormPage />
              </FormsGate>
            }
          />
          <Route path="/forms/:id/*" element={<FormDetailPage />} />

          {/* Cross-form approval queue */}
          <Route
            path="/approvals"
            element={
              <FormsGate allow={(p) => p.canApprove}>
                <ApprovalsPage />
              </FormsGate>
            }
          />

          {/* Integrations */}
          <Route
            path="/webhooks"
            element={
              <FormsGate allow={(p) => p.canManageWebhooks}>
                <WebhooksPage />
              </FormsGate>
            }
          />

          {/* Admin */}
          <Route
            path="/admin/settings"
            element={
              <FormsGate allow={(p) => p.canManageSettings}>
                <AdminSettingsPage />
              </FormsGate>
            }
          />
          <Route
            path="/admin/lookups"
            element={<Navigate to="/app/forms/admin/lookups/categories" replace />}
          />
          <Route
            path="/admin/lookups/:lookup"
            element={
              <FormsGate allow={(p) => p.canManageSettings}>
                <AdminLookupsPage />
              </FormsGate>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/forms" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
